import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Skeleton } from "../../../design-v2";
import { getWorkoutExecution } from "../api/workoutLogApi";
import {
  buildWorkoutJournal,
  formatTrainingDate,
  formatWorkoutStats,
  trainAgainPath,
  workoutReflection,
} from "../domain/workoutDetails";
import useConnectivity from "../hooks/useConnectivity";
import "../workoutDetails.css";

// Read-only journal of one SAVED workout (blueprint §15.10). The server read
// model is the only source: no local record is read, and nothing is editable.

const VIEW = Object.freeze({
  LOADING: "loading",
  READY: "ready",
  NOT_FOUND: "not-found",
  FORBIDDEN: "forbidden",
  OFFLINE: "offline",
  ERROR: "error",
});

function failureView(error, online) {
  // The server answers 404 for an execution that does not exist AND for one
  // that belongs to someone else: neither is ever distinguished or shown.
  if (error?.status === 404) return VIEW.NOT_FOUND;
  if (error?.status === 401 || error?.status === 403) return VIEW.FORBIDDEN;
  if (!online) return VIEW.OFFLINE;
  return VIEW.ERROR;
}

const FAILURE_COPY = Object.freeze({
  [VIEW.NOT_FOUND]: {
    title: "Workout not found",
    body: "This workout is not available.",
    retry: false,
  },
  [VIEW.FORBIDDEN]: {
    title: "Sign in to view this workout",
    body: "Your session is no longer valid for this workout.",
    retry: false,
  },
  [VIEW.OFFLINE]: {
    title: "Needs a connection",
    body: "Workout details load from the server.",
    retry: true,
  },
  [VIEW.ERROR]: {
    title: "Couldn’t load this workout",
    body: "Something went wrong. Try again.",
    retry: true,
  },
});

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export default function WorkoutDetailsScreen() {
  const { executionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { online } = useConnectivity();
  const [view, setView] = useState(VIEW.LOADING);
  const [details, setDetails] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setView(VIEW.LOADING);
    setDetails(null);
    getWorkoutExecution(executionId).then((response) => {
      if (!active) return;
      // Only a SAVED workout has a journal: an open or discarded execution is
      // not a saved workout.
      if (response?.execution?.status !== "SAVED") {
        setView(VIEW.NOT_FOUND);
        return;
      }
      setDetails(response);
      setView(VIEW.READY);
    }).catch((error) => {
      if (active) setView(failureView(error, typeof navigator === "undefined" || navigator.onLine !== false));
    });
    return () => {
      active = false;
    };
  }, [attempt, executionId]);

  // Plain history back; a cold deep link has no history and returns Home.
  const goBack = useCallback(() => {
    if (location.key && location.key !== "default") navigate(-1);
    else navigate("/", { replace: true });
  }, [location.key, navigate]);

  const execution = details?.execution || null;
  const journal = view === VIEW.READY ? buildWorkoutJournal(details) : [];
  const stats = execution ? formatWorkoutStats(execution) : [];
  const reflection = view === VIEW.READY ? workoutReflection(details) : null;
  const failure = FAILURE_COPY[view] || null;

  return (
    <section className="lz-workout-details" data-view={view} aria-labelledby="workout-details-title">
      <header className="lz-workout-details__header">
        <button
          type="button"
          className="lz-workout-details__back"
          aria-label="Back"
          onClick={goBack}
        >
          <BackIcon />
        </button>
        <p className="lz-workout-details__eyebrow">Workout Details</p>
      </header>

      {view === VIEW.LOADING ? (
        <div className="lz-workout-details__loading" aria-busy="true" data-testid="workout-details-loading">
          <h1 className="lz-v2-visually-hidden" id="workout-details-title">Loading workout</h1>
          <Skeleton shape="block" height={112} aria-label="Loading workout summary" />
          <Skeleton shape="block" height={96} />
          <Skeleton shape="block" height={96} />
        </div>
      ) : null}

      {failure ? (
        <Card className="lz-workout-details__failure" role="alert">
          <h1 id="workout-details-title">{failure.title}</h1>
          <p>{failure.body}</p>
          <div className="lz-workout-details__failure-actions">
            {failure.retry ? (
              <Button
                variant="primary"
                disabled={view === VIEW.OFFLINE && !online}
                onClick={() => setAttempt((value) => value + 1)}
              >
                Try again
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => navigate("/", { replace: true })}>
              Back to Home
            </Button>
          </div>
        </Card>
      ) : null}

      {view === VIEW.READY ? (
        <>
          <div className="lz-workout-details__summary">
            {execution.outcome === "EXTRA" ? (
              <p className="lz-workout-details__tag" data-tone="info">Extra Workout</p>
            ) : null}
            <h1 id="workout-details-title">{execution.title}</h1>
            <p className="lz-workout-details__date">{formatTrainingDate(execution.trainingDate)}</p>
            {execution.sourceLabel ? (
              <p className="lz-workout-details__source">{execution.sourceLabel}</p>
            ) : null}
            {stats.length ? (
              <p className="lz-workout-details__stats">{stats.join(" · ")}</p>
            ) : null}
          </div>

          {reflection ? (
            <section className="lz-workout-details__reflection" aria-labelledby="workout-details-reflection">
              <h2 id="workout-details-reflection">Your thoughts about this workout</h2>
              <p>{reflection}</p>
            </section>
          ) : null}

          <ol className="lz-workout-details__journal" aria-label="Exercises">
            {journal.map((entry) => (
              <li className="lz-workout-details__exercise" key={entry.key}>
                <h2>
                  {entry.name}
                  {entry.cardioValue ? (
                    <span className="lz-workout-details__cardio"> · {entry.cardioValue}</span>
                  ) : null}
                </h2>
                {entry.substitutedFrom ? (
                  <p className="lz-workout-details__substituted">for {entry.substitutedFrom}</p>
                ) : null}
                {entry.sets.length ? (
                  <ol className="lz-workout-details__sets" aria-label={`${entry.name} sets`}>
                    {entry.sets.map((set, index) => (
                      <li key={set.key}>
                        <span className="lz-workout-details__set-index" aria-hidden="true">
                          {index + 1}
                        </span>
                        <span className="lz-workout-details__set-value">{set.text}</span>
                      </li>
                    ))}
                  </ol>
                ) : null}
                {entry.notPerformedLine ? (
                  <p className="lz-workout-details__muted">{entry.notPerformedLine}</p>
                ) : null}
                {entry.notes ? (
                  <p className="lz-workout-details__notes">{entry.notes}</p>
                ) : null}
              </li>
            ))}
          </ol>

          <div className="lz-workout-details__footer">
            <Button
              variant="primary"
              size="lg"
              className="lz-workout-details__train-again"
              onClick={() => navigate(trainAgainPath(execution.executionId))}
            >
              Train again
            </Button>
          </div>
        </>
      ) : null}
    </section>
  );
}
